// Home.tsx
import React, { useState, useEffect, useRef, lazy, Suspense } from 'react';
import { motion } from 'framer-motion';
import { useSwipeable } from 'react-swipeable';

import { getNewsForCarousel, getNewsForCards, getTeamMembers } from '../../../firebase/firestoreService';
import { programService, Program as FirestoreProgram } from '../../../firebase/programFirestoreService';
import { cache } from '../../../utils/cacheManager';

import StatsSection from './components/StatsSection';
import CTASection from './components/CTASection';
import SectionDivider from './components/SectionDivider'; 
import ValuesSection from './components/ValuesSection';

// Import types
import { 
  NewsItem, 
  Program, 
  NewsCard, 
  TeamMember, 
  HomeStats, 
  LoadingState 
} from './components/types';

// Import helpers
import { 
  formatDate, 
  toMillis, 
  deriveStatusFromDates,
  getRandomReadTime,
  getBadgeGradient
} from './utils/homeHelpers';

// Import lazy-loaded components
const NewsCarousel = lazy(() => import('./components/NewsCarousel'));
const NewsCardsSection = lazy(() => import('./components/NewsCardsSection'));
const FeaturedProgramSection = lazy(() => import('./components/FeaturedProgramSection'));
const MoreFeaturedProgramsSection = lazy(() => import('./components/MoreFeaturedProgramsSection'));
const LeadershipSection = lazy(() => import('./components/LeadershipSection'));

const HOME_CAROUSEL_AUTOPLAY_MS = 7000;

const resolveNewsCategory = (newsItem: any): string => {
  const raw = newsItem?.category;
  if (typeof raw === 'string' && raw.trim()) {
    const normalized = raw.trim();
    if (normalized.toLowerCase() !== 'general') {
      return normalized;
    }
  }
  if (raw && typeof raw === 'object') {
    const fromObject =
      (typeof raw.label === 'string' && raw.label.trim()) ? raw.label :
      (typeof raw.name === 'string' && raw.name.trim()) ? raw.name :
      (typeof raw.value === 'string' && raw.value.trim()) ? raw.value :
      '';
    if (fromObject) return fromObject;
  }
  const tags = Array.isArray(newsItem?.tags) ? newsItem.tags : [];
  if (tags.length > 0 && typeof tags[0] === 'string' && tags[0].trim()) {
    return tags[0].trim();
  }
  return 'General';
};

// Ensure image URLs include sensible sizing & format params for faster mobile loads
const ensureOptimizedImage = (rawUrl: string | undefined | null, defaultUrl: string, width = 1200) => {
  try {
    const url = rawUrl && typeof rawUrl === 'string' && rawUrl.trim() ? rawUrl : defaultUrl;
    const separator = url.includes('?') ? '&' : '?';
    return `${url}${separator}auto=format&fit=crop&w=${width}&q=70&fm=webp`;
  } catch (e) {
    return defaultUrl;
  }
};
const Home: React.FC = () => {
  const [stats, setStats] = useState<HomeStats>({
    programs: 0,
    beneficiaries: 0,
    events: 0,
    volunteers: 0
  });
  
  const [news, setNews] = useState<NewsItem[]>([]);
  const [newsCards, setNewsCards] = useState<NewsCard[]>([]);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [latestProgram, setLatestProgram] = useState<Program | null>(null);
  const [featuredPrograms, setFeaturedPrograms] = useState<Program[]>([]);
  const [ceo, setCeo] = useState<TeamMember | null>(null);
  const [loading, setLoading] = useState<LoadingState>({
    news: true,
    newsCards: true,
    program: true,
    team: true
  });

  // attach this ref to the main scroll container and pass to SectionDivider
  const containerRef = useRef<HTMLDivElement | null>(null);
  
  useEffect(() => {
    const fetchHomeData = async () => {
      try {
        setLoading(prev => ({ ...prev, news: true, newsCards: true }));

        // Fetch carousel news. The service owns its cache so admin updates can invalidate it reliably.
        const carouselResult = await getNewsForCarousel(5);
        if (carouselResult.success && carouselResult.data) {
          const carouselNewsData = carouselResult.data.map((newsItem: any) => ({
            id: newsItem.id,
            title: newsItem.title,
            excerpt: newsItem.excerpt || 'Read more about this news...',
            imageUrl: ensureOptimizedImage(newsItem.imageUrl, 'https://images.unsplash.com/photo-1559028012-481c04fa702d?ixlib=rb-1.2.1', 1600),
            date: newsItem.date,
            author: newsItem.author || 'Foundation Team',
            category: resolveNewsCategory(newsItem),
            isProgram: false,
            isUpcoming: !!newsItem.isUpcoming,
            tags: newsItem.tags || [],
            content: newsItem.content
          }));
          setNews(carouselNewsData);
        }
        setLoading(prev => ({ ...prev, news: false }));

        // Fetch cards news. The service owns its cache so admin updates can invalidate it reliably.
        const cardsResult = await getNewsForCards(8);
        if (cardsResult.success && cardsResult.data) {
          const newsCardsData = cardsResult.data.map((newsItem: any, index: number) => {
            let dateIso: string | null = null;
            try {
              if (newsItem.date?.toDate) {
                dateIso = newsItem.date.toDate().toISOString();
              } else if (typeof newsItem.date === 'number') {
                dateIso = new Date(newsItem.date).toISOString();
              } else if (typeof newsItem.date === 'string') {
                dateIso = new Date(newsItem.date).toISOString();
              } else if (newsItem.date?.seconds) {
                dateIso = new Date(newsItem.date.seconds * 1000).toISOString();
              }
            } catch (e) {
              dateIso = null;
            }

            return {
              id: newsItem.id,
              title: newsItem.title,
              excerpt: newsItem.excerpt || (newsItem.content ? newsItem.content.substring(0, 120) + '...' : 'Read more...'),
              imageUrl: ensureOptimizedImage(
                newsItem.newsCardImageUrl || newsItem.imageUrl || `https://images.unsplash.com/photo-${1559028012 + index}-481c04fa702d`,
                `https://images.unsplash.com/photo-${1559028012 + index}-481c04fa702d`,
                800
              ),
              date: dateIso || newsItem.date || new Date().toISOString(),
              category: newsItem.category || 'Update',
              author: 'Foundation Team',
              readTime: getRandomReadTime(),
              badgeColor: getBadgeGradient(index),
              newsCardImagePositionX: typeof newsItem.newsCardImagePositionX === 'number' ? newsItem.newsCardImagePositionX : 50,
              newsCardImagePositionY: typeof newsItem.newsCardImagePositionY === 'number' ? newsItem.newsCardImagePositionY : 50
            };
          });
          setNewsCards(newsCardsData);
        }
        setLoading(prev => ({ ...prev, newsCards: false }));

        // Fetch programs (cached)
        let programs = await cache.get('home.programs.published.10', () => programService.getPublishedPrograms(10), 60 * 60 * 1000);
        let programStats = { totalPublished: programs.length } as any;

        if ((!programs || programs.length === 0)) {
          const freshPrograms = await programService.getPublishedPrograms(10);
          if (freshPrograms && freshPrograms.length > 0) {
            programs = freshPrograms;
            programStats = { totalPublished: freshPrograms.length } as any;
          }
        }

        const homepagePrograms = await cache.get<FirestoreProgram[]>(
          'home.programs.visible.16',
          () => programService.getHomepageVisiblePrograms(16),
          60 * 60 * 1000
        );

        if (programStats) {
          setStats(prev => ({ 
            ...prev, 
            programs: programStats.totalPublished || programs.length 
          }));
        }

        if (programs && programs.length > 0) {
          const sortedPrograms = [...programs].sort((a, b) => {
            if (a.isFeatured && !b.isFeatured) return -1;
            if (!a.isFeatured && b.isFeatured) return 1;
            const dateA = toMillis(a.startDate);
            const dateB = toMillis(b.startDate);
            return dateB - dateA;
          });
          
          // Get featured programs
          const featured = sortedPrograms
              .filter(p => p.isFeatured === true)
              .slice(0, 3)
              .map(program => {
                const derivedStatus = deriveStatusFromDates(program.startDate, program.endDate, program.status);
                return {
                  id: program.id || '',
                  title: program.title,
                  description: program.description,
                  date: formatDate(program.startDate),
                  startDate: program.startDate?.toDate?.()?.toISOString() || new Date().toISOString(),
                  endDate: program.endDate?.toDate?.()?.toISOString() || new Date().toISOString(),
                  imageUrl: program.imageUrl || 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
                  status: derivedStatus,
                  registrationLink: `/programs/${program.id}`,
                  isFeatured: program.isFeatured,
                  category: program.category,
                  location: program.location,
                  requiresRegistration: !!program.requiresRegistration,
                  registeredCount: program.registeredCount,
                  capacity: program.capacity
                } as Program;
              });
          
          setFeaturedPrograms(featured);
          
          // Get latest program
          const latestProgramData = sortedPrograms[0];
          if (latestProgramData) {
            const mappedProgram: Program = {
              id: latestProgramData.id || '',
              title: latestProgramData.title,
              description: latestProgramData.description,
              date: formatDate(latestProgramData.startDate),
              startDate: latestProgramData.startDate?.toDate?.()?.toISOString() || new Date().toISOString(),
              endDate: latestProgramData.endDate?.toDate?.()?.toISOString() || new Date().toISOString(),
              imageUrl: latestProgramData.imageUrl || 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1?ixlib=rb-4.0.3&auto=format&fit=crop&w=1920&q=80',
              newsCardImageUrl: latestProgramData.newsCardImageUrl || '',
              status: deriveStatusFromDates(latestProgramData.startDate, latestProgramData.endDate, latestProgramData.status),
              registrationLink: `/programs/${latestProgramData.id}`,
              isFeatured: latestProgramData.isFeatured,
              category: latestProgramData.category,
              location: latestProgramData.location,
              requiresRegistration: !!latestProgramData.requiresRegistration,
              registeredCount: latestProgramData.registeredCount,
              capacity: latestProgramData.capacity
            };
            setLatestProgram(mappedProgram);
          }
        }

        if (homepagePrograms && homepagePrograms.length > 0) {
          try {
            const toJSDate = (v: any): Date | null => {
              if (!v) return null;
              if (v instanceof Date) return v;
              if (v?.toDate && typeof v.toDate === 'function') return v.toDate();
              if (typeof v === 'number') return new Date(v);
              if (typeof v === 'string') return new Date(v);
              if (v?.seconds) return new Date(v.seconds * 1000);
              return null;
            };

            const programCardItems = homepagePrograms
              .filter(p => p.showInCards === true)
              .slice(0, 8)
              .map((p, index) => ({
                id: p.id || `program-${p.id}`,
                title: p.title,
                excerpt: p.description || '',
                imageUrl: ensureOptimizedImage((p as any).newsCardImageUrl || p.imageUrl || '', 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1', 800),
                date: toJSDate(p.startDate)?.toISOString() || new Date().toISOString(),
                category: p.category || 'Program',
                author: 'AIFCC',
                readTime: getRandomReadTime(),
                badgeColor: getBadgeGradient(index),
                isProgram: true,
                newsCardImagePositionX: typeof (p as any).newsCardImagePositionX === 'number' ? (p as any).newsCardImagePositionX : 50,
                newsCardImagePositionY: typeof (p as any).newsCardImagePositionY === 'number' ? (p as any).newsCardImagePositionY : 50
              } as NewsCard));

            if (programCardItems.length > 0) {
              setNewsCards(prev => {
                const existingIds = new Set(prev.map(x => x.id));
                const uniquePrograms = programCardItems.filter(item => !existingIds.has(item.id));
                const merged = [...uniquePrograms, ...prev];
                return merged.slice(0, 8);
              });
            }

            const programCarouselItems = homepagePrograms
              .filter(p => p.showInCarousel === true)
              .map(p => ({
                id: p.id || '',
                title: p.title,
                excerpt: p.description || '',
                imageUrl: ensureOptimizedImage(p.imageUrl || '', 'https://images.unsplash.com/photo-1523050854058-8df90110c9f1', 1600),
                date: toJSDate(p.startDate)?.toISOString() || new Date().toISOString(),
                startDate: toJSDate(p.startDate)?.toISOString() || '',
                endDate: toJSDate(p.endDate)?.toISOString() || '',
                author: 'AIFCC',
                category: p.category || 'Program',
                isProgram: true,
                showInCarousel: true,
                registrationLink: `/programs/${p.id}`,
                isUpcoming: (() => { const s = toJSDate(p.startDate); return s ? s > new Date() : false; })(),
                tags: p.tags || [],
                content: p.description || ''
              } as any));

            if (programCarouselItems.length > 0) {
              setNews(prev => {
                const existingIds = new Set(prev.map(x => x.id));
                const uniquePrograms = programCarouselItems.filter(item => !existingIds.has(item.id));
                return [...uniquePrograms, ...prev];
              });
            }
          } catch (e) {
            console.error('Error adding homepage-visible programs', e);
          }
        }
        setLoading(prev => ({ ...prev, program: false }));

        // Fetch team members (cached)
        const teamResult = await cache.get('home.team.members', () => getTeamMembers(), 24 * 60 * 60 * 1000);
        if (teamResult.success && teamResult.data) {
          const teamMembersData = teamResult.data as any[];
          const teamMembers: TeamMember[] = teamMembersData.map((member: any) => ({
            id: member.id || '',
            name: member.name || 'Team Member',
            position: member.position || '',
            bio: member.bio || '',
            imageUrl: member.imageUrl || '',
            role: member.position || '',
            department: ''
          }));

          const leadershipTitles = ['chair', 'ceo', 'chief', 'executive', 'founder', 'director'];
          const foundLeader = teamMembers.find(member => {
            const position = (member.position || '').toLowerCase();
            return leadershipTitles.some(title => position.includes(title));
          });

          if (foundLeader) {
            setCeo(foundLeader);
          } else if (teamMembers.length > 0) {
            setCeo(teamMembers[0]);
          }
        }
        setLoading(prev => ({ ...prev, team: false }));

      } catch (error) {
        console.error('Error fetching home data:', error);
        setLoading({
          news: false,
          newsCards: false,
          program: false,
          team: false
        });
      }
    };

    fetchHomeData();
  }, []);

  // Auto-rotate slides on a short, predictable cadence.
  useEffect(() => {
    if (news.length > 1) {
      const interval = setInterval(() => {
        setCurrentSlide((prev) => (prev + 1) % news.length);
      }, HOME_CAROUSEL_AUTOPLAY_MS);
      return () => clearInterval(interval);
    }
  }, [news.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % news.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) => (prev - 1 + news.length) % news.length);
  };

  const goToSlide = (index: number) => {
    setCurrentSlide(index);
  };

  // Debug logs
  useEffect(() => {
    console.log('🪪 newsCards state changed:', newsCards);
    console.log('🪪 loading state:', loading);
  }, [newsCards, loading]);

return (
    <div className="min-h-screen relative overflow-hidden">
      {/* SINGLE CONTINUOUS GRADIENT BACKGROUND */}
      <div className="fixed inset-0 -z-10">
        <div className="absolute inset-0 bg-gradient-to-b from-slate-900 via-indigo-950 to-purple-900"></div>
        
        {/* Subtle animated elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="hidden sm:block absolute top-1/4 left-1/4 w-96 h-96 bg-gradient-to-r from-cyan-500/5 to-blue-500/5 rounded-full blur-3xl animate-pulse motion-reduce:animate-none pointer-events-none" aria-hidden="true" />
          <div className="hidden sm:block absolute bottom-1/4 right-1/4 w-96 h-96 bg-gradient-to-r from-purple-500/5 to-pink-500/5 rounded-full blur-3xl animate-pulse delay-1000 motion-reduce:animate-none pointer-events-none" aria-hidden="true" />
        </div>
      </div>
      
      {/* Content - all sections are transparent */}
      <div className="relative z-10" ref={containerRef}>
        {/* News Carousel (lazy-loaded) */}
        {news.length > 0 && (
          <>
            <Suspense fallback={<div className="h-[360px] rounded-2xl bg-slate-800/30 flex items-center justify-center">Loading latest news...</div>}>
              <NewsCarousel
                news={news}
                loading={loading.news}
                currentSlide={currentSlide}
                nextSlide={nextSlide}
                prevSlide={prevSlide}
                goToSlide={goToSlide}
              />
            </Suspense>

            <ValuesSection />
            
            <SectionDivider 
              variant="scroll-grow" 
              color="cyan" 
              spacing="normal" 
              scrollContainerRef={containerRef}
            />
          </>
        )}

        {/* News Cards (lazy-loaded) */}
        <Suspense fallback={<div className="h-96 rounded-2xl bg-slate-800/20 flex items-center justify-center">Loading activities...</div>}>
          <NewsCardsSection
            newsCards={newsCards}
            loading={loading.newsCards}
          />
        </Suspense>
        
  <SectionDivider 
        variant="ripple" 
        color="purple" 
        spacing="wide"
        scrollContainerRef={containerRef}
      />
        {/* Stats */}
        <StatsSection stats={stats} />
        
          <SectionDivider 
        variant="scroll-stretch" 
        color="multi" 
        spacing="wide"
        scrollContainerRef={containerRef}
      />

        {/* Featured Program (lazy-loaded) */}
        <Suspense fallback={<div className="h-72 rounded-2xl bg-slate-800/20 flex items-center justify-center">Loading program...</div>}>
          <FeaturedProgramSection latestProgram={latestProgram} />
        </Suspense>
        
          <SectionDivider 
        variant="magnetic" 
        color="blue" 
        spacing="normal"
        scrollContainerRef={containerRef}
      />

        {/* More Featured Programs (lazy-loaded) */}
        <Suspense fallback={<div className="h-64 rounded-2xl bg-slate-800/20 flex items-center justify-center">Loading more programs...</div>}>
          <MoreFeaturedProgramsSection featuredPrograms={featuredPrograms} />
        </Suspense>
        
         <SectionDivider 
        variant="parallax" 
        color="multi" 
        spacing="tight"
        scrollContainerRef={containerRef}
      />

        {/* Leadership (lazy-loaded) */}
        <Suspense fallback={<div className="h-56 rounded-2xl bg-slate-800/20 flex items-center justify-center">Loading leadership...</div>}>
          <LeadershipSection />
        </Suspense>
        
        <SectionDivider 
        variant="particles" 
        color="cyan" 
        spacing="normal"
        scrollContainerRef={containerRef}
      />

        {/* CTA */}
        {/* <CTASection /> */}
      </div>
      
      {/* Add custom animations to CSS */}
      {/* <style jsx global>{`
        @keyframes shimmer {
          0% {
            transform: translateX(-100%);
          }
          100% {
            transform: translateX(100%);
          }
        }
        
        .animate-shimmer {
          animation: shimmer 2s infinite;
        }
        
        .delay-300 {
          animation-delay: 0.3s;
        }
      `}</style> */}
    </div>
  );
};

export default Home;
